import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/throttleTime';

import { of } from 'rxjs/observable/of';
import type { Observable } from 'rxjs/Observable';
import { catchError } from 'rxjs/operators/catchError';
import { map } from 'rxjs/operators/map';
import { mergeMap } from 'rxjs/operators/mergeMap';
import { tap } from 'rxjs/operators/tap';
import {
	fetchConfluencePermissions,
	fetchConfluenceSpace,
} from '../../../rest/whiteboards-entrypoint-experiment/index.tsx';
import { LOAD_APP_DATA_REQUEST } from '../../../state/actions/app/index.tsx';
import { setConfluenceData } from '../../../state/actions/whiteboards-entrypoint-experiment/index.tsx';
import type { ActionsObservable } from '../../../state/types.tsx';
import {
	getConfluenceData as getConfluenceCacheData,
	setConfluenceData as setConfluenceCacheData,
} from '../../../view/whiteboards-entrypoint-experiment/constants.tsx';

const hasPermissionsAndPersonalSpaceService: () => Observable<{
	hasOperations: boolean;
	hasPersonalSpace: boolean;
}> = () =>
	fetchConfluencePermissions().pipe(
		map((response) => ({
			hasOperations: (response?.operations && response?.operations?.length > 0) ?? false,
			hasPersonalSpace: !!response?.personalSpace,
		})),
		catchError(() =>
			of({
				hasOperations: false,
				hasPersonalSpace: false,
			}),
		),
	);

const hasConfluenceSpacesService = () =>
	fetchConfluenceSpace().pipe(
		map((response) => (response?.results && response?.results?.length > 0) ?? false),
		catchError(() => of(false)),
	);

export const loadWhiteboardsEntrypointConfluenceDataEpic = (action$: ActionsObservable) => {
	return action$
		.ofType(LOAD_APP_DATA_REQUEST)
		.throttleTime(1000)
		.switchMap(() => {
			const cachedValue = getConfluenceCacheData();
			if (cachedValue) {
				return of(setConfluenceData(cachedValue));
			}

			return hasPermissionsAndPersonalSpaceService().pipe(
				mergeMap(({ hasOperations, hasPersonalSpace }) => {
					if (hasPersonalSpace) {
						const data = {
							hasConfluenceAccess: hasOperations,
							hasConfluenceSpaces: true,
						};
						setConfluenceCacheData(data);
						return of(setConfluenceData(data));
					}

					return hasConfluenceSpacesService().pipe(
						tap((hasSpaces) => {
							setConfluenceCacheData({
								hasConfluenceAccess: hasOperations,
								hasConfluenceSpaces: hasSpaces,
							});
						}),
						map((hasSpaces) =>
							setConfluenceData({
								hasConfluenceAccess: hasOperations,
								hasConfluenceSpaces: hasSpaces,
							}),
						),
					);
				}),
			);
		})
		.catch(() => of(setConfluenceData({ hasConfluenceAccess: false, hasConfluenceSpaces: false })));
};
